import type { WorkspaceSearchResultType } from "./ResultTypeSwitch";

interface WorkspaceSearchLayoutInput {
  isSearching: boolean;
  resultType: WorkspaceSearchResultType;
  /** Workspaces whose name matches the search; undefined until the list has loaded. */
  workspaceCount: number | undefined;
  isLoadingWorkspaces: boolean;
}

export interface WorkspaceSearchLayout {
  /** The workspace heading and table are part of the page (all but "Dokumenter"). */
  showWorkspaces: boolean;
  /** "Alle": the table shows a capped preview of the name matches above the documents. */
  previewWorkspaces: boolean;
  /** The table draws its rows, or its "Ingen resultater" when there are none. */
  showWorkspaceRows: boolean;
  /** The documents whose title or text matches are listed. */
  showDocuments: boolean;
}

/**
 * What the Workspaces page shows for a search (LAR-235). "Alle" previews the workspaces
 * whose name matches above the matching documents; "Workspaces" and "Dokumenter" show one
 * of the two.
 */
export const getWorkspaceSearchLayout = ({
  isSearching,
  resultType,
  workspaceCount,
  isLoadingWorkspaces,
}: WorkspaceSearchLayoutInput): WorkspaceSearchLayout => {
  const showWorkspaces = !isSearching || resultType !== "documents";
  const previewWorkspaces = isSearching && resultType === "all";
  // A search often matches no workspace name. Under "Alle" the heading already says
  // "0 workspaces", and the table's centred "Ingen resultater" below it read as if the
  // whole search had found nothing, although the documents below had hits.
  const emptyPreview =
    previewWorkspaces && !isLoadingWorkspaces && workspaceCount === 0;

  return {
    showWorkspaces,
    previewWorkspaces,
    showWorkspaceRows: showWorkspaces && !emptyPreview,
    showDocuments: isSearching && resultType !== "workspaces",
  };
};
